import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, map, timeout, retry, tap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { SpeechResponse, SpeechError, StreamSpeechRequest, AudioFileResponse, SpeechTestResponse } from '../interfaces/speech-types';
// Legacy Chirp2StreamingService removed - use StreamingSpeechService instead

type Float32ArrayWithArrayBuffer = Float32Array & { buffer: ArrayBuffer };

/**
 * Google Cloud Speech-to-Text Service
 *
 * This service provides integration with Firebase Cloud Functions
 * that proxy Google Cloud Speech-to-Text API for secure, real-time dictation.
 */
@Injectable({
  providedIn: 'root',
})
export class GoogleSpeechService {
  private readonly functionsBaseUrl: string;
  private isAvailable$ = new BehaviorSubject<boolean>(false);
  private isInitialized = false;

  // Audio recording properties
  private mediaRecorder?: MediaRecorder;
  private audioChunks: Blob[] = [];
  private currentStream?: MediaStream;
  private voiceEnergySubject = new BehaviorSubject<number>(0);
  voiceEnergy$ = this.voiceEnergySubject.asObservable();

  private energyContext?: AudioContext;
  private energyAnalyser?: AnalyserNode;
  private energyData?: Float32ArrayWithArrayBuffer;
  private energySource?: MediaStreamAudioSourceNode;
  private energyAnimationFrame?: number;

  constructor(private http: HttpClient) {
    // Configure Firebase Functions URL - always use deployed functions for Google Speech
    // since we're not running Firebase Functions locally for development
    this.functionsBaseUrl = 'https://us-central1-healthbridge-meraki-dev.cloudfunctions.net';

    // Note: Service will initialize lazily when first used to avoid authentication issues during app startup
  }

  /**
   * Initialize the service and test connectivity
   */
  private async initializeService(): Promise<void> {
    try {
      console.log('🎤 GoogleSpeechService: Initializing service...');
      console.log('🎤 GoogleSpeechService: Testing connection to:', this.functionsBaseUrl);

      // Test if the service is available
      const testResult = await this.testConnection().toPromise();
      console.log('🎤 GoogleSpeechService: Test result:', testResult);

      this.isAvailable$.next(testResult?.success || false);
      this.isInitialized = true;

      if (testResult?.success) {
        console.log('✅ GoogleSpeechService: Service is available and ready');
      } else {
        console.warn('⚠️ GoogleSpeechService: Service test failed');
      }
    } catch (error) {
      console.warn('🎤 GoogleSpeechService: Service not available, falling back to browser API:', error);
      this.isAvailable$.next(false);
      this.isInitialized = true;
    }
  }

  /**
   * Check if Google Speech-to-Text is available
   */
  get isAvailable(): Observable<boolean> {
    return this.isAvailable$.asObservable();
  }

  /**
   * Test connection to Firebase Cloud Function
   */
  testConnection(): Observable<SpeechTestResponse> {
    // For Firebase callable functions, we need to call them differently
    return this.http
      .post<SpeechTestResponse>(
        `${this.functionsBaseUrl}/testgooglespeechsetup`,
        { data: {} }, // Firebase callable functions expect data in this format
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      .pipe(
        timeout(10000), // 10 second timeout
        retry(2),
        map(response => {
          // Firebase callable functions return data in a 'result' property
          return (response as any).result || response;
        }),
        catchError(error => {
          console.error('Speech service test failed:', error);
          return of({ success: false, message: 'Service unavailable' });
        })
      );
  }

  /**
   * Choose between streaming (Chirp 2) or batch processing
   */
  async startStreamingRecognition(
    sessionId: string,
    options: {
      patientId?: string;
      contextInfo?: {
        fieldContext: string;
        componentName: string;
        formFieldName: string;
      };
      sampleRate?: number;
      useStreaming?: boolean; // New option to choose streaming vs batch
    } = {}
  ): Promise<Observable<SpeechResponse>> {
    // Note: Streaming mode should use StreamingSpeechService instead
    // This service only handles batch processing
    console.log('🎤 Using batch processing');

    // Skip initialization during dictation to avoid authentication issues
    // The service will work without the availability check
    this.isInitialized = true;

    console.log('🎤 GoogleSpeechService: Starting batch recognition for session:', sessionId);
    console.log('🎤 GoogleSpeechService: Options:', options);

    try {
      // Get microphone access
      this.currentStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: options.sampleRate || 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      this.startVoiceEnergyTracking(this.currentStream);

      // Configure MediaRecorder for optimal speech recognition
      const mimeType = this.getBestMimeType();
      this.mediaRecorder = new MediaRecorder(this.currentStream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });

      this.audioChunks = [];

      // Return an Observable that will emit speech results
      return new Observable<SpeechResponse>(subscriber => {
        if (!this.mediaRecorder) {
          subscriber.error(new Error('MediaRecorder not initialized'));
          return;
        }

        this.mediaRecorder.ondataavailable = event => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onerror = event => {
          subscriber.error(new Error(`MediaRecorder error: ${event}`));
        };

        this.mediaRecorder.onstop = async () => {
          try {
            // Process the complete recording
            if (this.audioChunks.length > 0) {
              const audioBlob = new Blob(this.audioChunks, { type: mimeType });
              const audioFile = new File([audioBlob], `recording-${sessionId}.webm`, { type: mimeType });

              console.log('🎤 GoogleSpeechService: Processing audio file:', audioFile.name, 'Size:', audioFile.size, 'bytes');

              // Process using our Cloud Function
              this.processAudioFile(audioFile, sessionId, {
                patientId: options.patientId,
              }).subscribe({
                next: result => {
                  console.log('🎤 GoogleSpeechService: Converting result to SpeechResponse:', result);
                  // Convert AudioFileResponse to SpeechResponse format
                  const speechResponse: SpeechResponse = {
                    transcript: result.transcript,
                    confidence: result.confidence || 0,
                    isFinal: true,
                    sessionId: sessionId,
                    timestamp: result.processingTime || new Date().toISOString(),
                  };
                  console.log('🎤 GoogleSpeechService: Emitting speechResponse:', speechResponse);
                  console.log('🎤 GoogleSpeechService: Subscriber state - closed:', subscriber.closed);

                  // Check if subscriber is still active
                  if (subscriber.closed) {
                    console.log('🎤 GoogleSpeechService: ❌ Subscriber is closed, cannot emit result');
                    return;
                  }

                  console.log('🎤 GoogleSpeechService: ✅ Emitting to active subscriber');
                  subscriber.next(speechResponse);
                  subscriber.complete();
                },
                error: error => subscriber.error(error),
              });
            } else {
              subscriber.complete();
            }
          } catch (error) {
            subscriber.error(error);
          }
          this.cleanup();
        };

        // Start recording
        this.mediaRecorder.start();
      });
    } catch (error) {
      console.error('Failed to start streaming recognition:', error);
      this.stopVoiceEnergyTracking();
      throw error;
    }
  }

  /**
   * Stop streaming recognition
   */
  stopStreamingRecognition(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.stopVoiceEnergyTracking();
    this.cleanup();
  }

  /**
   * Process audio file (non-streaming)
   */
  processAudioFile(
    file: File,
    sessionId: string,
    options: {
      patientId?: string;
      userId?: string;
    } = {}
  ): Observable<AudioFileResponse> {
    console.log('🎤 GoogleSpeechService: Sending raw audio data to Cloud Function...');

    // Send raw binary data instead of multipart form data to avoid parsing issues
    const headers = {
      'Content-Type': 'audio/webm',
      'X-Session-ID': sessionId,
      'X-Patient-ID': options.patientId || '',
      'X-User-ID': options.userId || '',
      ...this.getAuthHeaders(),
    };

    return this.http
      .post<AudioFileResponse>(
        `${this.functionsBaseUrl}/processspeechfile`,
        file, // Send file directly as binary data
        { headers }
      )
      .pipe(
        timeout(120000), // 2 minute timeout for file processing
        retry(1),
        map(response => {
          console.log('✅ GoogleSpeechService: Received response from Cloud Function:', response);
          return response;
        }),
        catchError(error => {
          console.error('❌ GoogleSpeechService: Audio file processing failed:', error);
          return throwError(error);
        })
      );
  }

  // Note: Real-time streaming will be implemented in future version
  // For now we use batch processing which is more reliable

  /**
   * Get authentication headers for Firebase Functions
   */
  private getAuthHeaders(): { [key: string]: string } {
    // For now, we'll use basic headers
    // In production, you would add Firebase Auth token here
    return {
      'X-Requested-With': 'XMLHttpRequest',
      // Note: Content-Type will be set automatically by the browser for FormData
    };
  }

  /**
   * Get the best supported MIME type for audio recording
   */
  private getBestMimeType(): string {
    const mimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/wav'];

    for (const mimeType of mimeTypes) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        return mimeType;
      }
    }

    return 'audio/webm'; // fallback
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    this.stopVoiceEnergyTracking();

    if (this.currentStream) {
      this.currentStream.getTracks().forEach(track => track.stop());
      this.currentStream = undefined;
    }

    this.mediaRecorder = undefined;
    this.audioChunks = [];
  }

  private startVoiceEnergyTracking(stream: MediaStream): void {
    if (typeof window === 'undefined') {
      return;
    }

    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) {
      return;
    }

    this.stopVoiceEnergyTracking();

    try {
      this.energyContext = new AudioContextClass();
      this.energySource = this.energyContext.createMediaStreamSource(stream);
      this.energyAnalyser = this.energyContext.createAnalyser();
      this.energyAnalyser.fftSize = 256;
      this.energySource.connect(this.energyAnalyser);
      const buffer = new ArrayBuffer(this.energyAnalyser.fftSize * Float32Array.BYTES_PER_ELEMENT);
      this.energyData = new Float32Array(buffer) as Float32ArrayWithArrayBuffer;

      const updateEnergy = () => {
        if (!this.energyAnalyser || !this.energyData) {
          return;
        }

        const energyView = this.energyData as any;
        this.energyAnalyser.getFloatTimeDomainData(energyView);
        const level = this.calculateVoiceEnergyLevel(energyView as Float32Array);
        this.voiceEnergySubject.next(level);

        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
          this.energyAnimationFrame = window.requestAnimationFrame(updateEnergy);
        }
      };

      updateEnergy();
    } catch (error) {
      console.warn('⚠️ Unable to start voice energy tracking for GoogleSpeechService:', error);
      this.voiceEnergySubject.next(0);
    }
  }

  private stopVoiceEnergyTracking(): void {
    if (this.energyAnimationFrame && typeof window !== 'undefined' && window.cancelAnimationFrame) {
      window.cancelAnimationFrame(this.energyAnimationFrame);
    }

    this.energyAnimationFrame = undefined;

    if (this.energyAnalyser) {
      this.energyAnalyser.disconnect();
      this.energyAnalyser = undefined;
    }

    if (this.energySource) {
      this.energySource.disconnect();
      this.energySource = undefined;
    }

    if (this.energyContext) {
      this.energyContext.close().catch(() => {});
      this.energyContext = undefined;
    }

    this.energyData = undefined;
    this.voiceEnergySubject.next(0);
  }

  private calculateVoiceEnergyLevel(buffer: Float32Array): number {
    if (!buffer.length) {
      return 0;
    }

    let sumSquares = 0;
    for (let i = 0; i < buffer.length; i++) {
      const sample = buffer[i];
      sumSquares += sample * sample;
    }

    const rms = Math.sqrt(sumSquares / buffer.length);
    const threshold = 0.003;
    const normalized = Math.min(1, Math.max(0, (rms - threshold) * 12));
    return Number(normalized.toFixed(4));
  }

  /**
   * Check if the browser supports the required features
   */
  static isBrowserSupported(): boolean {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder && window.btoa);
  }

  /**
   * Get available audio input devices
   */
  async getAudioInputDevices(): Promise<MediaDeviceInfo[]> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter(device => device.kind === 'audioinput');
    } catch (error) {
      console.error('Failed to enumerate audio devices:', error);
      return [];
    }
  }

  /**
   * Destroy the service and cleanup resources
   */
  ngOnDestroy(): void {
    this.cleanup();
    this.isAvailable$.complete();
  }
}
